import React, { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  normalizeWatermarkSettings,
  getWatermarkStyles,
  calculateWatermarkSize,
  type RawWatermarkSettings
} from '../../lib/watermark-config';

interface WatermarkPreviewProps {
  watermarkEnabled: boolean;
  watermarkPosition: string;
  watermarkOpacity: number; // 0-100 (local state format)
  watermarkScale: number; // 50-200 (local state format)
  watermarkLogoUrl?: string | null;
  wyskoWatermarkEnabled: boolean;
  wyskoWatermarkPosition: string;
  wyskoWatermarkOpacity: number; // 0-100 (local state format)
  wyskoWatermarkScale: number; // 50-200 (local state format)
  wyskoLogoUrl?: string | null;
  sampleImageUrl?: string | null;
  isLoadingSample?: boolean;
}

export function WatermarkPreview({
  watermarkEnabled,
  watermarkPosition,
  watermarkOpacity,
  watermarkScale,
  watermarkLogoUrl,
  wyskoWatermarkEnabled,
  wyskoWatermarkPosition,
  wyskoWatermarkOpacity,
  wyskoWatermarkScale,
  wyskoLogoUrl,
  sampleImageUrl,
  isLoadingSample
}: WatermarkPreviewProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  // Normalize settings from local state format (0-100, 50-200)
  const rawSettings: RawWatermarkSettings = {
    watermark_enabled: watermarkEnabled,
    watermark_position: watermarkPosition,
    watermark_opacity: watermarkOpacity,
    watermark_scale: watermarkScale,
    watermark_logo_url: watermarkLogoUrl,
    wysko_watermark_enabled: wyskoWatermarkEnabled,
    wysko_watermark_position: wyskoWatermarkPosition,
    wysko_watermark_opacity: wyskoWatermarkOpacity,
    wysko_watermark_scale: wyskoWatermarkScale,
  };

  const normalized = normalizeWatermarkSettings(rawSettings, 'local');

  // Track actual rendered image dimensions using ResizeObserver
  useEffect(() => {
    if (!imgRef.current) return;

    const updateDimensions = () => {
      if (imgRef.current) {
        const { clientWidth, clientHeight } = imgRef.current;
        if (clientWidth > 0 && clientHeight > 0) {
          setImageDimensions({ width: clientWidth, height: clientHeight });
        }
      }
    };

    const handleLoad = () => updateDimensions();

    const img = imgRef.current;
    img.addEventListener('load', handleLoad);

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setImageDimensions({ width, height });
        }
      }
    });

    observer.observe(img);

    if (img.complete && img.naturalWidth > 0) {
      updateDimensions();
    }

    return () => {
      img.removeEventListener('load', handleLoad);
      observer.disconnect();
    };
  }, [sampleImageUrl]);

  // Reset dimensions when sample image changes
  useEffect(() => {
    setImageDimensions(null);
  }, [sampleImageUrl]);

  // Render a single watermark with proportional sizing
  const renderWatermark = (
    type: 'agency' | 'wysko',
    logoSrc: string | null | undefined
  ) => {
    const wmSettings = type === 'agency' ? normalized.agency : normalized.wysko;

    if (!wmSettings.enabled || !logoSrc || !imageDimensions) return null;

    const isSecondary = type === 'wysko' && normalized.agency.enabled && !!watermarkLogoUrl;
    const otherPosition = type === 'wysko' ? normalized.agency.position : undefined;

    // Calculate proportional size based on displayed image dimensions
    const size = calculateWatermarkSize(
      imageDimensions.width,
      imageDimensions.height,
      type,
      wmSettings.scale
    );

    const styles = getWatermarkStyles(
      imageDimensions.width,
      imageDimensions.height,
      wmSettings,
      isSecondary,
      otherPosition,
      type === 'agency' ? 10 : 11
    );

    return (
      <div style={styles}>
        <img
          src={logoSrc}
          alt=""
          style={{ maxHeight: `${size.maxHeight}px`, maxWidth: `${size.maxWidth}px` }}
          className="object-contain"
          draggable={false}
        />
      </div>
    );
  };

  // Container adapts to image, with max-height constraint
  return (
    <div className="bg-[#0a0a0a] rounded-lg border border-border flex items-center justify-center p-3 min-h-[200px] max-h-[400px]">
      {/* Loading State */}
      {isLoadingSample ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : sampleImageUrl ? (
        /* Image wrapper - inline-block sizes exactly to image */
        <div className="relative inline-block">
          <img
            ref={imgRef}
            src={sampleImageUrl}
            alt="Sample render"
            className="block rounded max-w-full object-contain"
            style={{ maxHeight: '350px' }}
            draggable={false}
          />

          {/* Watermarks overlay - fills the wrapper (same size as image) */}
          {imageDimensions && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden rounded">
              {/* Agency watermark */}
              {renderWatermark('agency', watermarkLogoUrl)}

              {/* Wysko watermark */}
              {renderWatermark('wysko', wyskoLogoUrl)}
            </div>
          )}
        </div>
      ) : (
        /* No Sample - Show Placeholder */
        <div className="flex items-center justify-center py-16">
          <div className="text-4xl font-bold text-gray-300 opacity-50">
            SAMPLE
          </div>
        </div>
      )}

      {/* Empty state when no watermarks enabled and no sample */}
      {!watermarkEnabled && !wyskoWatermarkEnabled && !sampleImageUrl && !isLoadingSample && (
        <div className="flex items-center justify-center py-16">
          <p className="text-xs text-muted-foreground">
            Włącz znak wodny aby zobaczyć podgląd
          </p>
        </div>
      )}
    </div>
  );
}
